import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";

type Product = {
  id: string;
  name: string;
  price: number;
  description?: string;
  stock?: number;
  trackStock?: boolean;
  isArchived?: boolean;
  category?: { id: string; name: string } | null;
};

export const searchProductSkill: Skill = {
  id: "searchProduct",
  description:
    "Busca productos por nombre en un restaurante. Devuelve los primeros 10 que coincidan, con precio, stock y categoría.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      query: {
        type: "string",
        description: "Texto a buscar en nombre/descripción del producto.",
      },
    },
    required: ["restaurantId", "query"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const query = String(args.query ?? "").trim();
    if (!restaurantId || !query) {
      return { ok: false, error: "Faltan restaurantId o query." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step(`Buscando "${query}"…`, { durationMs: 500 });
    }

    try {
      const params = new URLSearchParams();
      params.set("search", query);
      params.set("limit", "10");
      const { data } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/products?${params.toString()}`,
        ctx.accessToken,
      );
      const items: Product[] = data?.items ?? data?.data ?? data ?? [];
      const compact = items.slice(0, 10).map((p) => ({
        id: p.id,
        name: p.name,
        price: p.price,
        stock: p.trackStock ? p.stock : null,
        categoryId: p.category?.id ?? null,
        categoryName: p.category?.name ?? null,
        archived: p.isArchived ?? false,
      }));

      if (ctx.follow.enabled) {
        await ctx.follow.step(
          `✓ ${compact.length} resultado(s)`,
          { durationMs: 600, spinner: false },
        );
        ctx.follow.clear();
      }

      return {
        ok: true,
        result: { count: compact.length, items: compact },
        humanSummary: `Encontrados ${compact.length} producto(s) para "${query}".`,
      };
    } catch (err) {
      return {
        ok: false,
        error: (err as Error)?.message ?? "Error buscando productos.",
      };
    }
  },
};
